import { Injectable } from "@angular/core";

import { FileService, SecurityService } from "@tylertech/core-module";
import { DocumentSearchCriteria, LoginCredentials } from "@tylertech/tyler-api-types";

import { AttachmentModel } from "../interfaces";

import { DocumentService } from "./documentService.g";

@Injectable()
export class AttachmentService
{
    private readonly fileTypeMap: Map<string, string> = new Map([
        ["doc", "Microsoft Word Document"],
        ["docx", "Microsoft Word Document"],
        ["xls", "Excel Spreadsheet"],
        ["xlsx", "Excel Spreadsheet"],
        ["pdf", "Adobe PDF"],
        ["txt", "Text File"],
        ["rtf", "Text File"],
        ["ppt", "PowerPoint Presentation"],
        ["pptx", "PowerPoint Presentation"],
        ["csv", "Comma Separated Values"],
        ["xml", "Extensible Markup Language"],
        ["png", "Image(png)"],
        ["jpg", "Image(jpg)"],
        ["jpeg", "Image(jpeg)"],
        ["tiff", "Image(tiff)"],
        ["gif", "Image(gif)"],
    ]);

    constructor(
        private readonly $securityService: SecurityService,
        private readonly $fileService: FileService,
        private readonly $documentApiService: DocumentService,
    ) { }

    isSupported(extension: string): boolean
    {
        return this.fileTypeMap.has(extension);
    }

    getFileTypeName(extension: string): string
    {
        return this.fileTypeMap.get(extension);
    }

    getSupportedFileTypes(): ReadonlyArray<string>
    {
        return Array.from(this.fileTypeMap.keys());
    }

    get MaxFileSize(): number
    {
        return 1450000;
    }

    protected createAttachment(file: File): AttachmentModel
    {
        const type = file.name.split(".")
            .pop()
            ?.toLowerCase();

        const fileType = this.parseFileType(file);
        return {
            Id: undefined,
            Data: undefined,
            File: file,
            Filename: file.name,
            AddedDate: new Date(),
            AddedBy: this.$securityService.principle.Fullname,
            Type: `.${type}`,
            ShowType: fileType,
            IsNew: true,
            IsDeleted: false,
            Error: file.size > this.MaxFileSize
                || fileType === "Invalid",
            Errors: {
                fileSize: file.size > this.MaxFileSize,
                fileType: fileType === "Invalid",
            },
            ModifiedBy: undefined,
            ModifiedDate: undefined,
        };
    }

    ConvertToAttachments(files: ReadonlyArray<File>): Promise<Array<AttachmentModel>>
    {
        const attachments: Array<AttachmentModel> = files.map(file => this.createAttachment(file));
        const promises: Array<Promise<void>> = attachments
            .reduce(
                (promises, attachment) =>
                {
                    if (!attachment.Error)
                    {
                        promises.push(
                            this.readFileContents(attachment));
                    }
                    return promises;
                },
                []);

        return Promise.all(promises)
            .then(() =>
            {
                return attachments;
            });
    }

    Download(criteria: DocumentSearchCriteria, attachment?: AttachmentModel): Promise<void>
    {
        if (attachment?.Data)
        {
            const fileType = attachment.File
                ? attachment.File.type
                : attachment.Type;
            this.$fileService.openDocumentLink(attachment.Data, fileType, attachment.Filename);
            return Promise.resolve();
        }

        return this.$documentApiService.DownloadDocument(criteria)
            .then(x =>
            {
                this.$fileService.openDocumentLink(x.Result.Content, x.Result.MimeType, x.Result.Filename);
            });
    }

    private readFileContents(attachment: AttachmentModel): Promise<void>
    {
        return new Promise(
            (resolveFn, rejectFn) =>
            {
                const reader = new FileReader();

                reader.onload = (_event: Event) =>
                {
                    const uInt8Array = new Uint8Array(reader.result as ArrayBuffer);
                    let charCode = "";
                    for (let i = 0; i < uInt8Array.length; ++i)
                    {
                        charCode += String.fromCharCode(uInt8Array[i]);
                    }
                    const data = btoa(charCode);
                    console.info(
                        `COMPLETED LOADING FILE: ${attachment.File.name} as ${typeof data} size of payload: ${data.length}`
                    );
                    attachment.Data = data;
                };

                reader.onerror = reader.onabort = (_event: Event) =>
                {
                    rejectFn(`ERROR READING FILE: ${attachment.File.name}`);
                };

                reader.onloadend = (_event: Event) =>
                {
                    resolveFn();
                };

                reader.readAsArrayBuffer(attachment.File);
            });
    }

    private parseFileType(file: File): string
    {
        const nameParts = file.name.split(".");
        if (nameParts.length === 1)
        {
            return "Invalid";
        }

        let ftype = nameParts.pop();
        if (file.type.match("image"))
        {
            ftype = "Image (" + ftype.toUpperCase() + ")";
        }
        else if (this.isSupported(ftype))
        {
            ftype = this.getFileTypeName(ftype);
        }
        else
        {
            ftype = "Invalid";
        }
        return ftype;
    }
}
